import { formatMoney, formatPercent } from './dates';
import { coverageLevelLabels, type BenefitPlan, type CoverageLevel } from './types';

/** One short line for a plan's cost, for the list. Percentages are "calculated at payroll": the page never works out a dollar figure for them. */
export function costSummary(plan: Pick<BenefitPlan, 'contributionMethod' | 'employeeContributionAmount' | 'employerContributionAmount' | 'employeeContributionPercent' | 'employerContributionPercent' | 'coverageTiers'>): string {
  const parts: string[] = [];
  switch (plan.contributionMethod) {
    case 'NoPayrollContribution':
      return 'No payroll contribution';
    case 'FixedPerPayroll':
    case 'FixedPerMonth': {
      if (plan.employeeContributionAmount !== null) parts.push(`${formatMoney(plan.employeeContributionAmount)} employee`);
      if (plan.employerContributionAmount !== null) parts.push(`${formatMoney(plan.employerContributionAmount)} employer`);
      return `${parts.join(', ') || 'No amount set'} ${plan.contributionMethod === 'FixedPerMonth' ? 'each month' : 'each payroll'}`;
    }
    case 'TieredByCoverage':
      return `${plan.coverageTiers.length} coverage ${plan.coverageTiers.length === 1 ? 'level' : 'levels'}`;
    default: {
      if (plan.employeeContributionPercent !== null) parts.push(`${formatPercent(plan.employeeContributionPercent)} employee`);
      if (plan.employerContributionPercent !== null) parts.push(`${formatPercent(plan.employerContributionPercent)} employer`);
      return `${parts.join(', ') || 'No percent set'} (calculated at payroll)`;
    }
  }
}

/**
 * What an enrollment would do to pay, for the enrollment preview. Fixed amounts only: a percentage says "calculated at payroll" and a waiver says there is no
 * contribution. This is information for the person enrolling; the pay statement is built later by payroll from the stored election, never from this text.
 */
export function payrollEffect(plan: BenefitPlan, coverageLevel: CoverageLevel | null, waived: boolean): string[] {
  if (waived) return ['A waiver records that the employee declined this plan. Nothing is taken from pay.'];
  const lines: string[] = [];
  switch (plan.contributionMethod) {
    case 'NoPayrollContribution':
      return ['No payroll deduction. This plan carries no payroll contribution.'];
    case 'FixedPerPayroll':
    case 'FixedPerMonth': {
      const unit = plan.contributionMethod === 'FixedPerMonth' ? 'each month' : 'each payroll';
      lines.push(plan.employeeContributionAmount !== null ? `Employee: ${formatMoney(plan.employeeContributionAmount)} ${unit}.` : 'Employee: no amount.');
      if (plan.fundingModel !== 'EmployeePaid') lines.push(plan.employerContributionAmount !== null ? `Employer: ${formatMoney(plan.employerContributionAmount)} ${unit}.` : 'Employer: no amount.');
      return lines;
    }
    case 'TieredByCoverage': {
      const tier = plan.coverageTiers.find((t) => t.coverageLevel === coverageLevel);
      if (!tier) return ['Choose a coverage level to see the amount.'];
      lines.push(`Coverage: ${coverageLevelLabels[tier.coverageLevel]}.`);
      if (tier.employeeAmount !== null) lines.push(`Employee: ${formatMoney(tier.employeeAmount)} each payroll.`);
      if (tier.employerAmount !== null) lines.push(`Employer: ${formatMoney(tier.employerAmount)} each payroll.`);
      return lines;
    }
    default:
      return ['The amount is a percentage of pay: calculated at payroll.'];
  }
}

export type ElectionStatus = 'Enrolled' | 'Waived' | 'Upcoming' | 'Ended';

/** The server's reading of an election's dates: it has ended once its end date is today or earlier, it is upcoming until its start date, and a waiver in
 * effect is an election that records a decline. */
export function electionStatus(election: { effectiveStartDate: string; effectiveEndDate: string | null; isWaived: boolean }, today: string): ElectionStatus {
  if (election.effectiveEndDate !== null && election.effectiveEndDate <= today) return 'Ended';
  if (election.effectiveStartDate > today) return 'Upcoming';
  return election.isWaived ? 'Waived' : 'Enrolled';
}
